import React, { useState } from 'react';
import { Mail, MessageCircle, Send, MapPin, Phone, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    university: '',
    program: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nUniversity: ${formData.university}\nProgram: ${formData.program}\nMessage: ${formData.message}`;
    const whatsappUrl = `https://wa.me/919130411300?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-black relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* CTA Section First */}
        <div className="text-center mb-20">
          <div className="bg-gradient-to-r from-orange-500/20 to-transparent p-12 rounded-3xl border border-orange-500/30 glow-card max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white futuristic-text mb-6">
              Apply Now
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto mb-8 glow-line"></div>
            <p className="text-2xl text-gray-300 mb-4">Limited Seats Available</p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto">
              Join the next generation of AI innovators. Choose from our 14-day intensive bootcamp or 2-day weekend mastermind. Our next programs start soon, and spaces are filling up fast. 
              Don't miss your chance to transform your future with cutting-edge AI skills.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://discord.gg/nicestnoob_67014"
                target="_blank"
                rel="noopener noreferrer"
                className="neon-button-primary px-10 py-4 text-xl font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 group"
              >
                <MessageCircle className="group-hover:rotate-12 transition-transform" />
                Join Our Discord
              </a>
              <a
                href="https://wa.me/919130411300"
                target="_blank"
                rel="noopener noreferrer"
                className="neon-button-secondary px-10 py-4 text-xl font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 group"
              >
                <Phone className="group-hover:rotate-12 transition-transform" />
                Chat on WhatsApp
              </a>
              <button className="neon-button-secondary px-10 py-4 text-xl font-bold rounded-xl transition-all duration-300">
                Download Brochure
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/20 glow-card">
            <h3 className="text-3xl font-bold text-white futuristic-text mb-6">Get in Touch</h3>
            <p className="text-gray-300 mb-8">
              Ready to start your AI journey? Choose your perfect program and we'll get back to you within 24 hours.
            </p>

            {submitStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-6">
                <p className="text-green-400 font-semibold">🎉 Application submitted successfully!</p>
                <p className="text-green-300 text-sm mt-1">We'll contact you within 24 hours.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">
                    Preferred Program *
                  </label>
                  <select
                    name="program"
                    value={formData.program}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300"
                  >
                    <option value="">Select a program</option>
                    <option value="14-day-bootcamp"> 🤖 14-Day AI Bootcamp</option>
                    <option value="2-day-mastermind">🚀 2-Day AI Mastermind</option>
                    <option value="both">Interested in both programs</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  University/College
                </label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleInputChange}
                  className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300"
                  placeholder="Your institution"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full bg-black/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 resize-none"
                  placeholder="Tell us about your background and goals..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-3 ${
                  isSubmitting 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'neon-button-primary hover:scale-105'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Application
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/20 glow-card">
              <h3 className="text-2xl font-bold text-white futuristic-text mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Email</h4>
                    <p className="text-gray-400">hello@inskill.ai</p>
                    <p className="text-gray-500 text-sm">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Discord Community</h4>
                    <p className="text-gray-400">Join our student community</p>
                    <p className="text-gray-500 text-sm">24/7 peer support & mentorship</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Office Hours</h4>
                    <p className="text-gray-400">Mon - Fri: 9:00 AM - 6:00 PM IST</p>
                    <p className="text-gray-500 text-sm">Available for student support</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Location</h4>
                    <p className="text-gray-400">100% Online Program</p>
                    <p className="text-gray-500 text-sm">Access from anywhere in the world</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Quick Links */}
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/20 glow-card">
              <h3 className="text-2xl font-bold text-white futuristic-text mb-6">Quick Questions?</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-black/30 rounded-lg border border-gray-700 hover:border-orange-500/30 transition-colors cursor-pointer">
                  <h4 className="text-orange-500 font-medium">Do I need prior coding experience?</h4>
                  <p className="text-gray-400 text-sm mt-1">Basic Python knowledge helps, but we cover fundamentals.</p>
                </div>
                <div className="p-4 bg-black/30 rounded-lg border border-gray-700 hover:border-orange-500/30 transition-colors cursor-pointer">
                  <h4 className="text-orange-500 font-medium">What's the time commitment?</h4>
                  <p className="text-gray-400 text-sm mt-1">4-6 hours per day, flexible schedule options available.</p>
                </div>
                <div className="p-4 bg-black/30 rounded-lg border border-gray-700 hover:border-orange-500/30 transition-colors cursor-pointer">
                  <h4 className="text-orange-500 font-medium">Is financial aid available?</h4>
                  <p className="text-gray-400 text-sm mt-1">Yes! We offer scholarships and payment plans.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;